// src/TourManager.ts
import { driver } from "driver.js";

// src/validators.ts
var TourError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "TourError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
};
var InvalidConfigError = class extends TourError {
  constructor(message) {
    super(message);
    this.name = "InvalidConfigError";
  }
};
var ElementNotFoundError = class extends TourError {
  constructor(target, selector) {
    super(`El elemento con data-tour="${target}" (selector: "${selector}") no se encuentra en el DOM.`);
    this.name = "ElementNotFoundError";
    this.target = target;
  }
};
var TourAlreadyActiveError = class extends TourError {
  constructor() {
    super("Ya hay un recorrido activo. Debe detener el recorrido actual antes de iniciar uno nuevo.");
    this.name = "TourAlreadyActiveError";
  }
};
function getElementSelector(target) {
  return `[data-tour="${target}"]`;
}
function validateConfig(config) {
  if (!config) {
    throw new InvalidConfigError("La configuraci\xF3n del recorrido no puede ser nula o indefinida.");
  }
  if (typeof config !== "object") {
    throw new InvalidConfigError("La configuraci\xF3n del recorrido debe ser un objeto.");
  }
  if (!config.steps) {
    throw new InvalidConfigError('La configuraci\xF3n debe contener un arreglo de pasos ("steps").');
  }
  if (!Array.isArray(config.steps)) {
    throw new InvalidConfigError('La propiedad "steps" debe ser un arreglo.');
  }
  if (config.steps.length === 0) {
    throw new InvalidConfigError('El arreglo de pasos ("steps") no puede estar vac\xEDo.');
  }
  config.steps.forEach((step, index) => {
    if (!step) {
      throw new InvalidConfigError(`El paso en el \xEDndice ${index} es nulo o indefinido.`);
    }
    if (!step.target || typeof step.target !== "string" || step.target.trim() === "") {
      throw new InvalidConfigError(`El paso en el \xEDndice ${index} requiere un "target" v\xE1lido (string no vac\xEDo).`);
    }
    if (!step.title || typeof step.title !== "string" || step.title.trim() === "") {
      throw new InvalidConfigError(`El paso en el \xEDndice ${index} requiere un "title" v\xE1lido (string no vac\xEDo).`);
    }
    if (!step.description || typeof step.description !== "string" || step.description.trim() === "") {
      throw new InvalidConfigError(`El paso en el \xEDndice ${index} requiere una "description" v\xE1lida (string no vac\xEDo).`);
    }
  });
}
function validateElementExists(target) {
  const selector = getElementSelector(target);
  const element = document.querySelector(selector);
  if (!element || !(element instanceof HTMLElement)) {
    throw new ElementNotFoundError(target, selector);
  }
  return element;
}

// src/TourManager.ts
var _TourManager = class _TourManager {
  constructor() {
    this.driverInstance = null;
    this.currentConfig = null;
    this.filteredSteps = [];
    this.currentStepIndex = -1;
    this.isTourRunning = false;
  }
  /**
   * Returns the Singleton instance of TourManager.
   */
  static getInstance() {
    if (!_TourManager.instance) {
      _TourManager.instance = new _TourManager();
    }
    return _TourManager.instance;
  }
  /**
   * Starts a guided tour with the provided configuration.
   * @param config The tour configurations (steps and options).
   * @param force If true, bypasses checks like localStorage completion persistence.
   */
  startTour(config, force = false) {
    if (this.isTourRunning) {
      throw new TourAlreadyActiveError();
    }
    validateConfig(config);
    const options = config.options || {};
    if (options.persistKey && !force) {
      const isCompleted = localStorage.getItem(options.persistKey);
      if (isCompleted === "completed") {
        console.info(`[TourManager] El tour con persistKey "${options.persistKey}" ya ha sido completado y no se iniciar\xE1.`);
        return;
      }
    }
    this.filteredSteps = this.filterSteps(config.steps, options);
    if (this.filteredSteps.length === 0) {
      console.warn("[TourManager] Todos los pasos del tour fueron filtrados por roles o versiones.");
      return;
    }
    try {
      validateElementExists(this.filteredSteps[0].target);
    } catch (error) {
      if (error instanceof TourError) {
        console.error("[TourManager] No se pudo iniciar el tour:", error.message);
        throw error;
      }
      throw error;
    }
    this.currentConfig = config;
    this.isTourRunning = true;
    this.currentStepIndex = 0;
    const driverSteps = this.filteredSteps.map((step) => {
      const side = step.position && step.position !== "auto" ? step.position : void 0;
      return {
        element: getElementSelector(step.target),
        popover: {
          title: step.title,
          description: step.description,
          side
        }
      };
    });
    this.driverInstance = driver({
      showProgress: options.showProgress ?? true,
      allowClose: options.allowClose ?? true,
      doneBtnText: options.doneBtnText ?? "Finalizar",
      nextBtnText: options.nextBtnText ?? "Siguiente",
      prevBtnText: options.prevBtnText ?? "Anterior",
      allowKeyboardControl: options.keyboardControl ?? true,
      steps: driverSteps,
      // Hook when step starts highlighting
      onHighlightStarted: (element, step, opts) => {
        const activeIndex = this.driverInstance?.getActiveIndex() ?? -1;
        this.currentStepIndex = activeIndex;
        const currentStep = this.filteredSteps[activeIndex];
        if (currentStep) {
          try {
            validateElementExists(currentStep.target);
          } catch (err) {
            console.error("[TourManager] Error en el paso actual:", err);
            this.stopTour();
            return;
          }
          if (options.onStepChange) {
            options.onStepChange(currentStep, activeIndex);
          }
        }
      },
      // Hook when Driver.js closes / is destroyed
      onDestroyed: () => {
        const wasLastStep = this.currentStepIndex === this.filteredSteps.length - 1;
        this.isTourRunning = false;
        if (wasLastStep) {
          if (options.persistKey) {
            localStorage.setItem(options.persistKey, "completed");
          }
          if (options.onComplete) {
            options.onComplete();
          }
        } else {
          if (options.onCancel) {
            options.onCancel();
          }
        }
        this.resetState();
      }
    });
    if (options.onStart) {
      options.onStart();
    }
    this.driverInstance.drive();
  }
  /**
   * Programmatically closes the tour if active.
   */
  stopTour() {
    if (this.driverInstance) {
      this.driverInstance.destroy();
    }
    this.resetState();
  }
  /**
   * Programmatically moves to the next step.
   */
  nextStep() {
    if (this.driverInstance && this.isTourRunning) {
      this.driverInstance.moveNext();
    }
  }
  /**
   * Programmatically moves to the previous step.
   */
  previousStep() {
    if (this.driverInstance && this.isTourRunning) {
      this.driverInstance.movePrevious();
    }
  }
  /**
   * Helper to check if a tour is currently running.
   */
  isActive() {
    return this.isTourRunning;
  }
  /**
   * Resets local states.
   */
  resetState() {
    this.driverInstance = null;
    this.currentConfig = null;
    this.filteredSteps = [];
    this.currentStepIndex = -1;
    this.isTourRunning = false;
  }
  /**
   * Filters tour steps based on target audience roles and app versions.
   */
  filterSteps(steps, options) {
    return steps.filter((step) => {
      if (step.roles && step.roles.length > 0) {
        if (!options.currentRole || !step.roles.includes(options.currentRole)) {
          return false;
        }
      }
      if (step.minVersion && options.currentVersion) {
        if (!this.isVersionSupported(options.currentVersion, step.minVersion)) {
          return false;
        }
      }
      return true;
    });
  }
  /**
   * Simple helper to check if currentVersion >= minVersion.
   */
  isVersionSupported(current, minimum) {
    const parse = (v) => v.split(".").map((num) => parseInt(num, 10) || 0);
    const [cMajor, cMinor, cPatch] = parse(current);
    const [mMajor, mMinor, mPatch] = parse(minimum);
    if (cMajor !== mMajor) return cMajor > mMajor;
    if (cMinor !== mMinor) return cMinor > mMinor;
    return cPatch >= mPatch;
  }
};
_TourManager.instance = null;
var TourManager = _TourManager;

// src/index.ts
function startTour(config, force = false) {
  TourManager.getInstance().startTour(config, force);
}
function stopTour() {
  TourManager.getInstance().stopTour();
}
function nextStep() {
  TourManager.getInstance().nextStep();
}
function previousStep() {
  TourManager.getInstance().previousStep();
}
function isTourActive() {
  return TourManager.getInstance().isActive();
}
export {
  ElementNotFoundError,
  InvalidConfigError,
  TourAlreadyActiveError,
  TourError,
  TourManager,
  getElementSelector,
  isTourActive,
  nextStep,
  previousStep,
  startTour,
  stopTour,
  validateConfig,
  validateElementExists
};
